import { Alert, Stack } from '@mui/material';
import { t } from 'i18next';
import { FormEventHandler, useEffect, useState } from 'react';
import { Dialog, DialogBody } from '../../common/components/Modal/Modal';
import { getErrorMessage } from '../../common/errors/errors';
import { Interpolation } from '../../config/i18n/components/Interpolation';
import { OrganizationUsersRepository } from '../../organization/users/services/OrganizationUsersRepository';
import { ButtonBox, DangerButton, PrimaryButton, TertiaryButton } from '../components/Buttons';
import { CopyPassword } from '../components/CopyPassword';

type Props = {
  userId: number;
  username: string;
  onClosed: () => void;
};

export const ResetPasswordFlow = ({ userId, username, onClosed }: Props) => {
  const [userService] = useState(new OrganizationUsersRepository());
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      userService.cancelAllRequests();
    };
  }, [userService]);

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      const password = await userService.resetPassword(userId);
      setLoading(false);
      setPassword(password);
    } catch (e: unknown) {
      setError(getErrorMessage(e));
      setLoading(false);
    }
  };

  return (
    <Dialog title={t('resetUserPassword.title')} onDismiss={() => onClosed()}>
      <DialogBody>
        <Stack spacing={1.5} component="form" onSubmit={onSubmit}>
          {error && <Alert severity="error">{error}</Alert>}
          {password === null && (
            <>
              <Interpolation i18nKey="resetUserPassword.info.content" sx={{ color: 'grey.700' }} values={{ username }} />
              <ButtonBox>
                <TertiaryButton disabled={loading} onClick={onClosed}>
                  {t('resetUserPassword.info.back')}
                </TertiaryButton>
                <DangerButton autoFocus disabled={loading} type="submit">
                  {t('resetUserPassword.info.submit')}
                </DangerButton>
              </ButtonBox>
            </>
          )}
          {password !== null && (
            <>
              <Interpolation i18nKey="resetUserPassword.success.content" sx={{ color: 'grey.700' }} values={{ username }} />
              <CopyPassword password={password} size="large" />
              <ButtonBox>
                <PrimaryButton onClick={onClosed}>{t('resetUserPassword.success.close')}</PrimaryButton>
              </ButtonBox>
            </>
          )}
        </Stack>
      </DialogBody>
    </Dialog>
  );
};
